/**
 * Shatter-Yo — Main Entry Point
 * 
 * Sets up canvas, input, and runs the fixed-timestep game loop.
 * Physics updates at 120Hz, rendering at display refresh rate.
 */

import { C } from './data/constants.js';
import { Canvas } from './core/Canvas.js';
import { Input } from './core/Input.js';
import { Game } from './core/Game.js';

// --- Initialize ---

const canvasEl = document.getElementById('game');
const canvas = new Canvas(canvasEl);
const input = new Input(canvas);
const game = new Game();
const ctx = canvas.ctx;

input.onTapCallback = (x, y) => game.handleDirectTap(x, y);

// --- Game Loop (fixed timestep) ---

let lastTime = 0;
let accumulator = 0;

function gameLoop(timestamp) {
  // Calculate delta time in seconds
  if (lastTime === 0) lastTime = timestamp;
  const deltaTime = Math.min((timestamp - lastTime) / 1000, 0.1); // cap at 100ms to prevent spiral
  lastTime = timestamp;

  // Apply slow-motion factor to accumulator
  accumulator += deltaTime * game.camera.slowMo;

  // Fixed timestep physics updates
  while (accumulator >= C.TICK_RATE) {
    game.update(input);
    accumulator -= C.TICK_RATE;
  }

  // Render
  canvas.clear();
  game.render(ctx);

  // Reset per-frame input state
  input.endFrame();

  // Next frame
  requestAnimationFrame(gameLoop);
}

// --- Start ---

// Unlock AudioContext and start Title music on first user interaction
const unlockAudio = () => {
  game.audio.init();
  if (game.state === 'TITLE' && !game.audio.isPlayingMusic && game.audio.musicEnabled) {
    game.audio.playTrack('title');
  }
};
document.addEventListener('pointerdown', unlockAudio, { once: true });
document.addEventListener('touchstart', unlockAudio, { once: true, passive: true });
document.addEventListener('click', unlockAudio, { once: true });
document.addEventListener('keydown', unlockAudio, { once: true });

// Prevent default touch behaviors on the entire document (pull-to-refresh, etc.)
document.addEventListener('touchmove', (e) => {
  if (e.target === canvasEl) e.preventDefault();
}, { passive: false });

// Start the game loop
requestAnimationFrame(gameLoop);
